/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 *
 * Quick Access -- a phone-style app grid. Groups (Service.groupName) show as
 * iOS-like folder tiles; tapping one opens that folder inside the widget
 * (back arrow to return). Ungrouped services sit on the main grid directly.
 * Edit mode: drag to reorder, per-tile edit/delete, add.
 */

import React, { useCallback, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ChevronLeftIcon, CheckIcon, PencilIcon, PlusIcon } from "@heroicons/react/24/outline";
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Service, updateService } from "@/api/services";
import { useHomeLayout } from "@/hooks/useHomeLayout";
import { useLanguage } from "@/utils/language";
import { GhAppTile, GhFolderTile } from "./widgets/GhTiles";

type GridItem =
  | { key: string; kind: "folder"; name: string; services: Service[] }
  | { key: string; kind: "app"; service: Service };

interface QuickAccessWidgetProps {
  services: Service[];
  isLoading: boolean;
  onAddService: () => void;
  onEditService: (s: Service) => void;
  onDeleteService: (s: Service) => void;
}

const Sortable: React.FC<{
  id: string;
  editing: boolean;
  render: (dragHandle: Record<string, unknown>) => React.ReactNode;
}> = ({ id, editing, render }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: !editing,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };
  return (
    <div ref={setNodeRef} style={style}>
      {render({ ...attributes, ...listeners })}
    </div>
  );
};

export const QuickAccessWidget: React.FC<QuickAccessWidgetProps> = ({
  services,
  isLoading,
  onAddService,
  onEditService,
  onDeleteService,
}) => {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const { reorder, getOrderedItems } = useHomeLayout();
  const [editing, setEditing] = useState(false);
  const [openGroup, setOpenGroup] = useState<string | null>(null);

  const { groups, gridItems } = useMemo(() => {
    const byGroup = new Map<string, Service[]>();
    const ungrouped: Service[] = [];
    for (const svc of services) {
      if (svc.groupName) {
        const list = byGroup.get(svc.groupName) ?? [];
        list.push(svc);
        byGroup.set(svc.groupName, list);
      } else {
        ungrouped.push(svc);
      }
    }
    for (const list of byGroup.values()) list.sort((a, b) => a.sortOrder - b.sortOrder);
    ungrouped.sort((a, b) => a.sortOrder - b.sortOrder);

    const items: GridItem[] = [
      ...[...byGroup.entries()].map(
        ([name, groupServices]): GridItem => ({
          key: `group:${name}`,
          kind: "folder",
          name,
          services: groupServices,
        }),
      ),
      ...ungrouped.map(
        (service): GridItem => ({ key: `svc:${service.id}`, kind: "app", service }),
      ),
    ];
    return { groups: byGroup, gridItems: getOrderedItems(items) };
  }, [services, getOrderedItems]);

  const folderServices = openGroup ? (groups.get(openGroup) ?? []) : [];

  const persist = useMutation({
    mutationFn: (ordered: Service[]) =>
      Promise.all(
        ordered.map((svc, sortOrder) =>
          updateService(svc.id, {
            id: svc.id,
            name: svc.name,
            url: svc.url,
            iconType: svc.iconType,
            iconValue: svc.iconValue,
            groupName: svc.groupName,
            sortOrder,
            enabled: svc.enabled,
            showLabel: svc.showLabel,
          }),
        ),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["services"] }),
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const reorderGrid = useCallback(
    (e: DragEndEvent) => {
      const { active, over } = e;
      if (over && active.id !== over.id) {
        reorder(gridItems.map((i) => i.key), active.id as string, over.id as string);
      }
    },
    [gridItems, reorder],
  );

  const reorderFolder = useCallback(
    (e: DragEndEvent) => {
      const { active, over } = e;
      if (!over || active.id === over.id) return;
      const ids = folderServices.map((s) => `svc:${s.id}`);
      const from = ids.indexOf(active.id as string);
      const to = ids.indexOf(over.id as string);
      if (from < 0 || to < 0) return;
      const next = [...folderServices];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      persist.mutate(next);
    },
    [folderServices, persist],
  );

  const header = (
    <div className="gh-w-head">
      {openGroup ? (
        <button type="button" className="gh-qa-back" onClick={() => setOpenGroup(null)}>
          <ChevronLeftIcon className="h-4 w-4" />
          <span className="gh-qa-back-name">{openGroup}</span>
        </button>
      ) : (
        <span className="gh-label">Quick Access</span>
      )}
      <div className="gh-qa-actions">
        <button
          type="button"
          className={`gh-btn ${editing ? "gh-btn--on" : ""}`}
          onClick={() => setEditing((v) => !v)}
        >
          {editing ? <CheckIcon className="h-3.5 w-3.5" /> : <PencilIcon className="h-3.5 w-3.5" />}
          {editing ? t("done") : t("edit")}
        </button>
        <button type="button" className="gh-btn" onClick={onAddService}>
          <PlusIcon className="h-3.5 w-3.5" />
          Add
        </button>
      </div>
    </div>
  );

  if (!isLoading && services.length === 0) {
    return (
      <section className="gh-w">
        {header}
        <div className="gh-empty">{t("noServicesHint")}</div>
      </section>
    );
  }

  // A single DndContext lives for the entire lifetime of the widget. Splitting
  // into two contexts (one per view) caused the PointerSensor to lose its
  // active node when the folder opened, because the old context unmounted
  // while a pointerdown was still in progress → @dnd-kit error boundary.
  return (
    <section className="gh-w">
      {header}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={openGroup ? reorderFolder : reorderGrid}
      >
        {openGroup ? (
          <SortableContext
            items={folderServices.map((s) => `svc:${s.id}`)}
            strategy={rectSortingStrategy}
          >
            <div className="gh-qa gh-qa--folder">
              {folderServices.map((svc) => (
                <Sortable
                  key={svc.id}
                  id={`svc:${svc.id}`}
                  editing={editing}
                  render={(dh) => (
                    <GhAppTile
                      service={svc}
                      editing={editing}
                      dragHandle={dh}
                      onEdit={onEditService}
                      onDelete={onDeleteService}
                    />
                  )}
                />
              ))}
            </div>
          </SortableContext>
        ) : (
          <SortableContext items={gridItems.map((i) => i.key)} strategy={rectSortingStrategy}>
            <div className="gh-qa">
              {gridItems.map((item) => (
                <Sortable
                  key={item.key}
                  id={item.key}
                  editing={editing}
                  render={(dh) =>
                    item.kind === "folder" ? (
                      <GhFolderTile
                        name={item.name}
                        services={item.services}
                        editing={editing}
                        dragHandle={dh}
                        onOpen={() => setOpenGroup(item.name)}
                      />
                    ) : (
                      <GhAppTile
                        service={item.service}
                        editing={editing}
                        dragHandle={dh}
                        onEdit={onEditService}
                        onDelete={onDeleteService}
                      />
                    )
                  }
                />
              ))}
            </div>
          </SortableContext>
        )}
      </DndContext>
    </section>
  );
};
